import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  Button,
  Card,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Fab,
  Tooltip,
  Chip,
  FormControl,
} from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import TextField from '@mui/material/TextField';
import { ERROR_KEY, RESET_DATE_RANGE } from '../../constants';
import LoadingButton from '@mui/lab/LoadingButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { ADMIN_PATH, PROJECT_PATH } from '../../url';

import KeycloakService from '../../services/KeycloakService';
import { useDispatch, useSelector } from 'react-redux';
import GenericFilterDrawer from '../../components/GenericFilter/GenericFilterDrawer';
import {
  resetFilterDataState,
  setFilterConfig,
} from '../../modules/filter/filterAction';
import { cardComponentCollapse } from '../../modules/cardState/cardAction';
import HttpService from '../../services/HttpService';
import './Notes.css';

const _axios = HttpService.getAxiosClient();
const tenantId = process.env.REACT_APP_TENANT_ID;

const buttonStyles = {
  margin: '7%',
  color: 'white',
  width: '5%',
  height: '19%',
  '&:hover': {
    backgroundColor: '#5f7ada',
  },
};

const Notes = (props) => {
  const { employee } = props;
  const [noteModal, setNoteModal] = useState(false);
  const [addNoteModal, setAddNoteModal] = useState(false);
  const [deleteNoteModal, setDeleteNoteModal] = useState(false);
  const [editNoteModal, setEditNoteModal] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState({
    content: '',
    title: '',
    tenantId: tenantId,
  });
  const [updatedNote, setUpdatedNote] = useState({});
  const [deleteNote, setDeleteNote] = useState({});
  const [options, setOptions] = useState({});
  const [projectArray, setProjectArray] = useState([]);
  const [IsContentError, setIsContentError] = useState(false);
  const [IsTitleError, setIsTitleError] = useState(false);
  const [IsProjectError, setIsProjectError] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [resetFilter, setResetFilter] = useState(false);
  const filterState = useSelector((state) => state.filterState);
  const dispatch = useDispatch();

  const handleNotes = () => {
    setNoteModal(true);
    fetchNotes();
  };

  const handleAddNoteModal = () => {
    setAddNoteModal(true);
    setNoteModal(false);
  };

  const onCancel = () => {
    setNewNote({
      content: '',
      title: '',
      tenantId: tenantId,
    });
    setIsContentError(false);
    setIsTitleError(false);
    setIsProjectError(false);
    setAddNoteModal(false);
    setNoteModal(true);
  };

  const onSave = () => {
    if (!newNote.title) {
      setIsTitleError(true);
      return;
    }
    if (!newNote.project) {
      setIsTitleError(false);
      setIsProjectError(true);

      return;
    }
    if (!newNote.content) {
      setIsTitleError(false);
      setIsProjectError(false);
      setIsContentError(true);
      return;
    }
    setIsTitleError(false);
    setIsContentError(false);
    setIsProjectError(false);
    saveNoteToBackend(newNote);
  };

  // Create a new note function
  const saveNoteToBackend = async (note) => {
    try {
      const response = await _axios.post(
        `${PROJECT_PATH}/projects/notes/newPost`,
        note,
        {
          params: {
            employeeId: employee.id,
            authorId: KeycloakService.getUserDetail(),
            projectId: note.project.projectId,
          },
        },
      );

      if (response.status === 200) {
        console.log('Note saved successfully');
        setNewNote({
          content: '',
          title: '',
          tenantId: tenantId,
        });
        resetThings();
        setAddNoteModal(false);
        setNoteModal(true);
      } else {
        console.error('Error saving note. Status:', response.status);
      }
    } catch (error) {
      console.error('Error saving note:', error.message);
    }
  };

  const onEditClick = (note) => {
    setUpdatedNote({
      notePostId: note.notePostId,
      content: note.content,
      title: note.title,
      projectId: note.projectId,
      projectName: note.projectName,
    });
    setNoteModal(false);
    setEditNoteModal(true);
  };

  const onDeleteIconClick = (note) => {
    setDeleteNote(note);
    setNoteModal(false);
    setDeleteNoteModal(true);
  };

  const handleNoDelete = () => {
    setDeleteNoteModal(false);
    setNoteModal(true);
  };

  // Delete a note function
  const onDeleteClick = async () => {
    try {
      const response = await _axios.delete(
        `${ADMIN_PATH}/notes/${deleteNote.notePostId}/delete`,
        {
          params: {
            employeeId: employee.id,
            tenantId: process.env.REACT_APP_TENANT_ID,
          },
        },
      );

      if (response.status === 200) {
        console.log('Successfully deleted note');
        setNoteModal(true);
        setDeleteNoteModal(false);
        if (
          Object.keys(filterState).length === 0 ||
          (filterState.startDate === '' &&
            filterState.endDate === '' &&
            filterState.projectName === '')
        ) {
          fetchNotes();
        } else {
          fetchFilteredNotes();
        }
      } else {
        console.error('Failed to delete note. Status:', response.status);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  // Edit a note function
  const onSaveClick = async () => {
    if (!updatedNote.title) {
      setIsTitleError(true);
      return;
    }
    if (!updatedNote.projectName) {
      setIsTitleError(false);
      setIsProjectError(true);

      return;
    }
    if (!updatedNote.content) {
      setIsTitleError(false);
      setIsProjectError(false);
      setIsContentError(true);
      return;
    }
    setIsTitleError(false);
    setIsContentError(false);
    setIsProjectError(false);

    try {
      const response = await _axios.put(
        `${ADMIN_PATH}/notes/${updatedNote.notePostId}/update/${KeycloakService.getUserDetail()}`,
        updatedNote,
      );

      if (response.status === 200) {
        console.log('Note updated successfully');
        setUpdatedNote({});
        setNoteModal(true);
        setEditNoteModal(false);
        if (
          Object.keys(filterState).length === 0 ||
          (filterState.startDate === '' &&
            filterState.endDate === '' &&
            filterState.projectName === '')
        ) {
          fetchNotes();
        } else {
          fetchFilteredNotes();
        }
      } else {
        console.error('Failed to update note. Status:', response.status);
      }
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const cancelEditable = () => {
    setEditNoteModal(false);
    setIsContentError(false);
    setIsTitleError(false);
    setIsProjectError(false);
    setNoteModal(true);
    setUpdatedNote({});
  };

  //format date
  const timeformatter = (datetimeString) => {
    const dateObj = new Date(datetimeString);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;
    return formattedDate;
  };

  //Get all notes function
  const fetchNotes = async () => {
    setNotes([]);
    setExpandedIndex();

    try {
      const response = await _axios.get(
        `${PROJECT_PATH}/projects/notes/getAllNotes`,
        {
          params: {
            employeeId: employee.id,
          },
        },
      );
      response.data.map((item) => {
        const note = {
          notePostId: item.notePostId,
          authorId: item.author.id,
          content: item.content,
          title: item.title,
          projectName: item.project.projectName,
          employeeId: item.employee.id,
          createdAt: timeformatter(item.createdAt),
          updatedAt: timeformatter(item.updatedAt),
          createdBy: item.postCreatedBy,
          updatedBy: item.postUpdatedBy,
        };
        setNotes((prevNotes) => [...prevNotes, note]);
      });
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  //Get filtered notes function
  const fetchFilteredNotes = async () => {
    setNotes([]);
    setExpandedIndex();
    const params = {
      employeeId: employee.id,
    };

    if (filterState.startDate) {
      params.startDate = filterState.startDate;
    }

    if (filterState.endDate) {
      params.endDate = filterState.endDate;
    }

    if (filterState.projectName) {
      params.projectName = filterState.projectName;
    }

    try {
      const response = await _axios.get(
        `${PROJECT_PATH}/projects/notes/filter`,
        {
          params: params,
        },
      );
      response.data.map((item) => {
        const note = {
          notePostId: item.notePostId,
          authorId: item.author.id,
          content: item.content,
          title: item.title,
          projectName: item.project.projectName,
          employeeId: item.employee.id,
          createdAt: timeformatter(item.createdAt),
          updatedAt: timeformatter(item.updatedAt),
          createdBy: item.postCreatedBy,
          updatedBy: item.postUpdatedBy,
        };
        setNotes((prevNotes) => [...prevNotes, note]);
      });
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const filterConfiguration = {
    dropdownField: [['Search Project', 'project', 'projectName', 'id']],
  };

  const resetThings = () => {
    for (let arrayOfArrays of filterConfiguration.dropdownField) {
      setOptions({ ...options, [arrayOfArrays[1]]: [] });
    }
    dispatch(setFilterConfig(filterConfiguration));
    dispatch({
      type: RESET_DATE_RANGE,
    });
    dispatch(resetFilterDataState());
    dispatch(cardComponentCollapse());
  };

  useEffect(() => {
    resetThings();
    setResetFilter(true);
  }, []);

  const getOptions = () => {
    return { ...options, project: projectArray };
  };

  const handleCloseNoteModal = (setShowModal) => () => {
    setShowModal(false);
    resetThings();
  };

  useEffect(() => {
    if (employee?.id) {
      const fetchProjects = async () => {
        try {
          const response = await _axios.get(
            `${PROJECT_PATH}/users/${employee.id}/projects`,
          );
          const data = response.data;
          setProjectArray(data.content);
        } catch (error) {
          console.error('Error fetching projects:', error);
        }
      };
      fetchProjects();
    }
  }, [employee]);

  const handleAccordionChange = (index) => {
    setExpandedIndex(index === expandedIndex ? null : index);
  };

  useEffect(() => {
    if (employee?.id) {
      if (
        Object.keys(filterState).length === 0 ||
        (filterState.startDate === '' &&
          filterState.endDate === '' &&
          filterState.projectName === '')
      ) {
        fetchNotes();
      } else {
        fetchFilteredNotes();
      }
    }
  }, [filterState]);

  const renderModalContentForNotes = (
    message,
    setShowModal,
    actionFunction,
  ) => {
    return (
      <Box>
        <Box className="boxStyleForNotes">
          <div style={{ display: 'flex' }}>
            <h6
              style={{
                margin: '1.5rem 1.5rem 2rem 1.5rem',
                color: '#4d6cd9',
                borderBottom: '1px solid #4d6cd9',
                display: 'flex',
                width: 'fit-content',
                padding: '5px',
              }}
            >
              {message}
            </h6>

            <GenericFilterDrawer options={getOptions()} />
          </div>
          {notes.length !== 0 ? (
            <div style={{ height: '70%', overflowY: 'auto' }}>
              {notes.map((note, index) => (
                <Accordion
                  key={index}
                  style={{ margin: '15px 30px' }}
                  expanded={index === expandedIndex}
                  onChange={() => {
                    handleAccordionChange(index);
                  }}
                >
                  <AccordionSummary expandIcon={<KeyboardArrowDownIcon />}>
                    <Grid container alignItems="center" justify="space-between">
                      <Grid item xs={4}>
                        <Typography style={{ marginLeft: '5px' }}>
                          {note.title}
                        </Typography>
                      </Grid>
                      <Grid item xs={4} style={{ textAlign: 'center' }}>
                        <Chip
                          label={note.createdAt}
                          color="secondary"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={4}
                        style={{ textAlign: 'right', paddingRight: '8px' }}
                      >
                        <Typography>{note.projectName}</Typography>
                      </Grid>
                    </Grid>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Card variant="outlined" sx={{ mb: 1 }}>
                      <div style={{ padding: '10px 10px' }}>
                        <div
                          style={{
                            overflowX: 'visible',
                            width: '100%',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                            }}
                          >
                            <div
                              style={{
                                alignItems: 'center',
                                display: 'flex',
                                width: '100%',
                                justifyContent: 'space-between',
                              }}
                            >
                              <h6
                                style={{
                                  flex: 1,
                                  display: 'flex',
                                  margin: '3px 0px',
                                  paddingLeft: '10px',
                                  alignItems: 'center',
                                }}
                              >
                                {note.content}
                              </h6>
                              {KeycloakService.hasRole(['admin']) && (
                                <div
                                  style={{
                                    alignItems: 'center',
                                    marginRight: '10px',
                                    display: 'flex',
                                  }}
                                >
                                  <EditIcon
                                    color="primary"
                                    className="action-icon-cursor"
                                    onClick={() => onEditClick(note)}
                                  />
                                  <DeleteIcon
                                    sx={{
                                      color: '#9c27b0',
                                      marginLeft: '20px',
                                    }}
                                    className="action-icon-cursor"
                                    onClick={() => onDeleteIconClick(note)}
                                  />
                                </div>
                              )}
                            </div>
                          </Box>
                        </div>
                      </div>
                    </Card>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Chip
                        label={`Created by: ${note.createdBy}`}
                        variant="outlined"
                        sx={{ border: 'none', color: 'gray' }}
                      />
                      <Chip
                        label={`Updated by: ${note.updatedBy}`}
                        variant="outlined"
                        sx={{ border: 'none', color: 'gray' }}
                      />
                    </div>
                  </AccordionDetails>
                </Accordion>
              ))}
            </div>
          ) : (
            <div
              style={{
                height: '70%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <p style={{ color: 'gray' }}>No notes available</p>
            </div>
          )}
          <Tooltip
            title={
              <Typography sx={{ fontSize: '0.8rem' }}>Add Note</Typography>
            }
            placement="top"
          >
            <Fab
              color="primary"
              size="small"
              style={{
                position: 'absolute',
                top: '16px',

                right: '20px',
                cursor: 'pointer',
              }}
              onClick={() => {
                handleAddNoteModal();
              }}
            >
              <NoteAddIcon />
            </Fab>
          </Tooltip>

          <Button
            variant="outlined"
            style={{
              margin: '10px',

              position: 'absolute',
              bottom: '10px',
              right: '10px',
            }}
            color={ERROR_KEY}
            onClick={handleCloseNoteModal(setShowModal)}
          >
            Close
          </Button>
        </Box>
      </Box>
    );
  };

  const renderModalContentForAddingNotes = (message) => {
    return (
      <Box className="boxStyleForAddNotes">
        <h6
          style={{
            margin: '1.5rem 1.5rem 2rem 1.5rem',
            color: '#4d6cd9',
            borderBottom: '1px solid #4d6cd9',
            display: 'flex',
            width: 'fit-content',
            padding: '5px',
          }}
        >
          {message}
        </h6>
        <Box>
          <Box sx={{ padding: '10px 22px' }}>
            <Box sx={{ display: 'flex', width: '100%' }}>
              <Box sx={{ width: '50%', marginRight: 1 }}>
                <TextField
                  name="noteTitle"
                  value={newNote.title}
                  onChange={(e) =>
                    setNewNote({ ...newNote, title: e.target.value })
                  }
                  required
                  id="standard-required"
                  label="Note Title"
                  error={IsTitleError}
                  rows={1}
                />
              </Box>
              <FormControl sx={{ width: '50%', marginLeft: 1 }}>
                <InputLabel id="projectTitle-label" required>
                  Project Title
                </InputLabel>
                <Select
                  labelId="projectTitle-label"
                  id="projectTitle"
                  name="projectTitle"
                  value={newNote.project}
                  error={IsProjectError}
                  onChange={(e) =>
                    setNewNote({ ...newNote, project: e.target.value })
                  }
                  required
                  label="Project Title"
                >
                  {projectArray.length === 0 && (
                    <MenuItem value="" disabled>
                      No Projects
                    </MenuItem>
                  )}
                  {projectArray.map((project) => (
                    <MenuItem key={project.projectId} value={project}>
                      {project.projectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: '100%', mt: 2 }}>
              <TextField
                name="noteContent"
                value={newNote.content}
                onChange={(e) =>
                  setNewNote({ ...newNote, content: e.target.value })
                }
                required
                id="standard-required"
                label="Note Content"
                error={IsContentError}
                multiline
                rows={9}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              m: 3,
            }}
          >
            <Button variant="outlined" color={ERROR_KEY} onClick={onCancel}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              onClick={() => onSave()}
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderModalContentForEditingNotes = (message) => {
    return (
      <Box className="boxStyleForAddNotes">
        <h6
          style={{
            margin: '1.5rem 1.5rem 2rem 1.5rem',
            color: '#4d6cd9',
            borderBottom: '1px solid #4d6cd9',
            display: 'flex',
            width: 'fit-content',
            padding: '5px',
          }}
        >
          {message}
        </h6>
        <Box>
          <Box sx={{ padding: '10px 22px' }}>
            <Box sx={{ display: 'flex', width: '100%' }}>
              <Box sx={{ width: '50%', marginRight: 1 }}>
                <TextField
                  name="noteTitle"
                  defaultValue={updatedNote.title}
                  onChange={(e) =>
                    setUpdatedNote({ ...updatedNote, title: e.target.value })
                  }
                  required
                  id="standard-required"
                  label="Note Title"
                  error={IsTitleError}
                  rows={1}
                />
              </Box>
              <FormControl sx={{ width: '50%', marginLeft: 1 }}>
                <InputLabel id="projectTitle-label" required>
                  Project Title
                </InputLabel>
                <Select
                  labelId="projectTitle-label"
                  id="projectTitle"
                  name="projectTitle"
                  defaultValue={updatedNote.projectName}
                  error={IsProjectError}
                  onChange={(e) =>
                    setUpdatedNote({
                      ...updatedNote,
                      projectName: e.target.value,
                    })
                  }
                  required
                  label="Project Title"
                >
                  {projectArray.length === 0 && (
                    <MenuItem value="" disabled>
                      No Projects
                    </MenuItem>
                  )}
                  {projectArray.map((project) => (
                    <MenuItem
                      key={project.projectId}
                      value={project.projectName}
                    >
                      {project.projectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: '100%', mt: 2 }}>
              <TextField
                name="noteContent"
                defaultValue={updatedNote.content}
                onChange={(e) =>
                  setUpdatedNote({ ...updatedNote, content: e.target.value })
                }
                required
                id="standard-required"
                label="Note Content"
                error={IsContentError}
                multiline
                rows={9}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              m: 3,
            }}
          >
            <Button
              variant="outlined"
              color={ERROR_KEY}
              onClick={cancelEditable}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              onClick={() => onSaveClick()}
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderModalContentForDeletingNote = () => {
    return (
      <Box style={{ display: 'flex', alignItems: 'center' }}>
        <Box className="boxStyleForDeleteNote">
          <Typography sx={{ paddingTop: '40px' }}>
            Delete this note "{deleteNote.title}"?
          </Typography>
          <Button
            style={{ ...buttonStyles, backgroundColor: 'red' }}
            onClick={handleNoDelete}
          >
            No
          </Button>
          <Button
            style={{ ...buttonStyles, backgroundColor: '#4d6cd9' }}
            onClick={() => onDeleteClick()}
          >
            Yes
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <div>
      <Tooltip
        title={<Typography sx={{ fontSize: '0.8rem' }}>Notes</Typography>}
        placement="top"
      >
        <Fab
          color="primary"
          size="small"
          onClick={() => {
            handleNotes();
          }}
        >
          <TextSnippetIcon />
        </Fab>
      </Tooltip>
      <Modal
        open={noteModal}
        onClose={() => {
          setNoteModal(false);
          resetThings();
        }}
      >
        {renderModalContentForNotes('Notes', setNoteModal)}
      </Modal>

      <Modal
        open={addNoteModal}
        onClose={() => {
          onCancel();
          resetThings();
        }}
      >
        {renderModalContentForAddingNotes('Add Note')}
      </Modal>

      <Modal
        open={editNoteModal}
        onClose={() => {
          setEditNoteModal(false);
          // resetThings();
        }}
      >
        {renderModalContentForEditingNotes('Edit Note')}
      </Modal>

      <Modal open={deleteNoteModal} onClose={() => setDeleteNoteModal(false)}>
        {renderModalContentForDeletingNote()}
      </Modal>
    </div>
  );
};

export default Notes;
